/**
 * Video Generation Service
 * Handles AI video generation using Veo API
 */

import { makeAuthenticatedRequest } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export interface VideoGenerationRequest {
  prompt: string;
  duration: number; // seconds (5-60)
  aspectRatio?: '16:9' | '9:16' | '1:1';
  model?: string;
}

export interface VideoGenerationResponse {
  success: boolean;
  data?: {
    operationId: string;
    status: string;
    estimatedCost: string;
    prompt: string;
    duration: number;
    aspectRatio: string;
  };
  message?: string;
  error?: string;
}

export interface VideoStatusResponse {
  success: boolean;
  data?: {
    operationId: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    done: boolean;
    videoReady?: boolean;
    videoName?: string;
    error?: string;
  };
  message?: string;
  error?: string;
}

export interface VideoDownloadResponse {
  success: boolean;
  data?: {
    operationId: string;
    firebaseUrl: string; // Firebase Storage URL
  };
  message?: string;
  error?: string;
}

export interface VideoHistoryItem {
  id: number;
  prompt: string;
  duration: number;
  aspect_ratio: string;
  operation_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
  completed_at: string | null;
  estimatedCost: string;
}

export interface VideoHistoryResponse {
  success: boolean;
  data?: {
    videos: VideoHistoryItem[];
    total: number;
    limit: number;
    offset: number;
  };
  message?: string;
  error?: string;
}

/**
 * Generate a new video
 */
export const generateVideo = async (request: VideoGenerationRequest): Promise<VideoGenerationResponse> => {
  return await makeAuthenticatedRequest('/videos/generate', {
    method: 'POST',
    body: JSON.stringify(request)
  });
};

/**
 * Check video generation status
 */
export const checkVideoStatus = async (operationId: string): Promise<VideoStatusResponse> => {
  // URL encode the operation ID since it contains forward slashes
  const encodedOperationId = encodeURIComponent(operationId);
  return await makeAuthenticatedRequest(`/videos/status/${encodedOperationId}`, {
    method: 'GET'
  });
};

/**
 * Download completed video
 */
export const downloadVideo = async (operationId: string): Promise<VideoDownloadResponse> => {
  // URL encode the operation ID since it contains forward slashes
  const encodedOperationId = encodeURIComponent(operationId);
  return await makeAuthenticatedRequest(`/videos/download/${encodedOperationId}`, {
    method: 'GET'
  });
};

/**
 * Get user's video generation history
 */
export const getVideoHistory = async (
  limit: number = 20, 
  offset: number = 0, 
  autoUpdate: boolean = false
): Promise<VideoHistoryResponse> => {
  return await makeAuthenticatedRequest(
    `/videos/history?limit=${limit}&offset=${offset}&autoUpdate=${autoUpdate}`, 
    {
      method: 'GET'
    }
  );
};

/**
 * Delete a video generation record
 */
export const deleteVideoRecord = async (operationId: string): Promise<{ success: boolean; message?: string }> => {
  // URL encode the operation ID since it contains forward slashes
  const encodedOperationId = encodeURIComponent(operationId);
  return await makeAuthenticatedRequest(`/videos/${encodedOperationId}`, {
    method: 'DELETE'
  });
};

/**
 * Calculate estimated cost for video generation
 */
export const calculateCost = (duration: number, model: 'fast' | 'standard' = 'fast'): string => {
  const pricePerSecond = model === 'fast' ? 0.40 : 0.75;
  const cost = duration * pricePerSecond;
  return `$${cost.toFixed(2)}`;
};

/**
 * Download video file from Firebase URL to user's device
 */
export const downloadVideoFile = (firebaseUrl: string, filename: string = 'video.mp4'): void => {
  const link = document.createElement('a');
  link.href = firebaseUrl;
  link.download = filename;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Poll video status with automatic retries
 */
export const pollVideoStatusUntilComplete = async (
  operationId: string,
  onProgress?: (status: string) => void,
  maxAttempts: number = 60,
  intervalMs: number = 5000
): Promise<VideoStatusResponse> => {
  let attempts = 0;

  while (attempts < maxAttempts) {
    try {
      const statusResponse = await checkVideoStatus(operationId);
      
      if (statusResponse.data) {
        onProgress?.(statusResponse.data.status);

        if (statusResponse.data.done) {
          return statusResponse;
        }
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      attempts++;
    } catch (error) {
      console.error('Error polling video status:', error);
      throw error;
    }
  }

  throw new Error('Video generation timeout. Please check status manually.');
};

